import React, { useState, useRef, useEffect, useCallback } from "react";
import ZoomableImage from "../../ui/zoomable-image";
import styles from "./appointment-detail-modal.module.css";
import Modal from "../../ui/modal";
import FormSection from "../../screens/form-section";
import Button from "../../ui/button";
import Input from "../../ui/input";
import SignatureCanvas from "react-signature-canvas";
import { MyIcons } from "../../icons/my-icons";
import ContactItem from "../../screens/contact-item";
import {
  getInfoCitaOperario,
  subirPresupuesto,
  subirFotos,
  subirComentarios,
  subirFirma,
  cerrarCita,
} from "../../../services/apiService";
import { showSuccessToast, showErrorToast } from "../../../utils/notifications";
import { API_BASE_URL } from "../../../config";

const AppointmentDetailModal = ({ isOpen, onClose, citaId, onUpdate, isHistory = false }) => {
  const [appointmentInfo, setAppointmentInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [closing, setClosing] = useState(false);
  const [comment, setComment] = useState("");
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [selectedBudgetFiles, setSelectedBudgetFiles] = useState(null);
  const [selectedPhotoFiles, setSelectedPhotoFiles] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filesModalOpen, setFilesModalOpen] = useState(false);
  const [filesModalCategory, setFilesModalCategory] = useState(null);
  const sigCanvas = useRef(null);
  const fileInputRefBudget = useRef(null);
  const fileInputRefPhotos = useRef(null);

  const loadAppointmentInfo = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getInfoCitaOperario(citaId);
      console.log("Appointment info loaded:", data);
      setAppointmentInfo(data);
    } catch (error) {
      console.error("Error loading appointment info:", error);
      showErrorToast("Error al cargar la información de la cita");
    } finally {
      setLoading(false);
    }
  }, [citaId]);

  useEffect(() => {
    if (isOpen && citaId) {
      loadAppointmentInfo();
    }
  }, [isOpen, citaId, loadAppointmentInfo]);

  const handleBudgetFileSelect = (event) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      setSelectedBudgetFiles(files);
    }
  };

  const handleClearBudgetFiles = () => {
    setSelectedBudgetFiles(null);
    if (fileInputRefBudget.current) {
      fileInputRefBudget.current.value = "";
    }
  };

  const handleUploadBudget = async () => {
    if (!selectedBudgetFiles || selectedBudgetFiles.length === 0) return;

    setUploading(true);
    try {
      const formData = new FormData();
      for (let i = 0; i < selectedBudgetFiles.length; i++) {
        formData.append("files", selectedBudgetFiles[i]);
      }

      await subirPresupuesto(citaId, formData);
      showSuccessToast("Presupuesto subido correctamente");
      setSelectedBudgetFiles(null);
      if (fileInputRefBudget.current) {
        fileInputRefBudget.current.value = "";
      }
      await loadAppointmentInfo();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Error uploading budget:", error);
      showErrorToast("Error al subir el presupuesto");
    } finally {
      setUploading(false);
    }
  };

  const handlePhotoFileSelect = (event) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      setSelectedPhotoFiles(files);
    }
  };

  const handleClearPhotoFiles = () => {
    setSelectedPhotoFiles(null);
    if (fileInputRefPhotos.current) {
      fileInputRefPhotos.current.value = "";
    }
  };

  const handleUploadPhotos = async () => {
    if (!selectedPhotoFiles || selectedPhotoFiles.length === 0) return;

    setUploading(true);
    try {
      const formData = new FormData();
      for (let i = 0; i < selectedPhotoFiles.length; i++) {
        formData.append("files", selectedPhotoFiles[i]);
      }

      await subirFotos(citaId, formData);
      showSuccessToast("Fotos subidas correctamente");
      setSelectedPhotoFiles(null);
      if (fileInputRefPhotos.current) {
        fileInputRefPhotos.current.value = "";
      }
      await loadAppointmentInfo();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Error uploading photos:", error);
      showErrorToast("Error al subir las fotos");
    } finally {
      setUploading(false);
    }
  };

  const handleUploadComments = async () => {
    if (!comment.trim()) {
      showErrorToast("Por favor, ingrese un comentario");
      return;
    }

    setUploading(true);
    try {
      await subirComentarios(citaId, comment);
      showSuccessToast("Comentario subido correctamente");
      setComment("");
      await loadAppointmentInfo();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Error uploading comments:", error);
      showErrorToast("Error al subir el comentario");
    } finally {
      setUploading(false);
    }
  };

  const handleUploadSignature = async () => {
    if (!sigCanvas.current || sigCanvas.current.isEmpty()) {
      showErrorToast("Por favor, dibuje una firma");
      return;
    }

    setUploading(true);
    try {
      // Convert signature to blob
      const dataUrl = sigCanvas.current.toDataURL("image/png");
      const blob = await (await fetch(dataUrl)).blob();
      
      const formData = new FormData();
      formData.append("files", blob, "firma.png");

      await subirFirma(citaId, formData);
      showSuccessToast("Firma subida correctamente");
      setShowSignaturePad(false);
      sigCanvas.current.clear();
      await loadAppointmentInfo();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Error uploading signature:", error);
      showErrorToast("Error al subir la firma");
    } finally {
      setUploading(false);
    }
  };

  const clearSignature = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
    }
  };

  const handleCloseCita = async () => {
    if (!window.confirm("¿Está seguro de que desea cerrar esta cita?")) {
      return;
    }

    setClosing(true);
    try {
      await cerrarCita(citaId);
      showSuccessToast("Cita cerrada correctamente");
      if (onUpdate) onUpdate();
      onClose();
    } catch (error) {
      console.error("Error closing appointment:", error);
      showErrorToast("Error al cerrar la cita");
    } finally {
      setClosing(false);
    }
  };

  const handleFileClick = (file) => {
    setSelectedFile(file);
  };

  const handleCloseViewer = () => {
    setSelectedFile(null);
  };

  const handleOpenFilesModal = (category) => {
    setFilesModalCategory(category);
    setFilesModalOpen(true);
  };

  const handleCloseFilesModal = () => {
    setFilesModalOpen(false);
    setFilesModalCategory(null);
    setSelectedFile(null);
  };

  const getFilesForCategory = () => {
    if (!filesModalCategory || !appointmentInfo) return [];
    switch (filesModalCategory) {
      case 'presupuestos':
        return appointmentInfo.archivosPresupuestos || [];
      case 'fotos':
        return appointmentInfo.archivosFotos || [];
      case 'firmas':
        return appointmentInfo.archivosFirmas || [];
      case 'visibles':
        return appointmentInfo.archivosVisibles || [];
      default:
        return [];
    }
  };

  const getFileIcon = (fileName) => {
    if (!fileName || typeof fileName !== 'string') {
      return <MyIcons name="fileFilled" width={48} height={48} color="var(--dark-grey)" />;
    }
    const ext = fileName.split('.').pop()?.toLowerCase() || '';
    const iconMap = {
      pdf: <MyIcons name="fileFilled" width={48} height={48} color="#dc2626" />,
      jpg: <MyIcons name="imageFilled" width={48} height={48} color="#059669" />,
      jpeg: <MyIcons name="imageFilled" width={48} height={48} color="#059669" />,
      png: <MyIcons name="imageFilled" width={48} height={48} color="#059669" />,
      doc: <MyIcons name="fileFilled" width={48} height={48} color="#2563eb" />,
      docx: <MyIcons name="fileFilled" width={48} height={48} color="#2563eb" />,
    };
    return iconMap[ext] || <MyIcons name="fileFilled" width={48} height={48} color="var(--dark-grey)" />;
  };

  const isImageFile = (fileName) => {
    if (!fileName || typeof fileName !== 'string') return false;
    const ext = fileName.split('.').pop()?.toLowerCase() || '';
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'heic', 'heif'].includes(ext);
  };

  const isVideoFile = (fileName) => {
    if (!fileName || typeof fileName !== 'string') return false;
    const ext = fileName.split('.').pop()?.toLowerCase() || '';
    return ['mp4', 'avi', 'mov', 'wmv', 'mkv', 'webm', 'flv', 'm4v'].includes(ext);
  };

  const getFileUrl = (file) => {
    if (file.url.startsWith('http')) {
      return file.url;
    }
    const relativeUrl = file.url.startsWith('/') ? file.url : `/${file.url}`;
    return `${API_BASE_URL}${relativeUrl}`;
  };

  const FileThumbnail = ({ file }) => {
    const [thumbnailUrl, setThumbnailUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
      let mounted = true;
      let objectUrl = null;

      const loadThumbnail = async () => {
        try {
          const fileUrl = getFileUrl(file);
          const token = localStorage.getItem('authToken');
          const res = await fetch(fileUrl, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
          });

          if (!res.ok) {
            throw new Error(`Error ${res.status}`);
          }

          const blob = await res.blob();
          objectUrl = URL.createObjectURL(blob);
          
          if (!mounted) {
            URL.revokeObjectURL(objectUrl);
            return;
          }
          
          setThumbnailUrl(objectUrl);
          setLoading(false);
        } catch (err) {
          console.error('Error loading thumbnail:', err);
          if (mounted) {
            setError(true);
            setLoading(false);
          }
        }
      };

      loadThumbnail();

      return () => {
        mounted = false;
        if (objectUrl) URL.revokeObjectURL(objectUrl);
      };
    }, [file]);

    if (loading) {
      return (
        <div className={styles.thumbnailLoading}>
          <span style={{ fontSize: '12px', color: 'var(--dark-grey)' }}>Cargando...</span>
        </div>
      );
    }

    if (error || !thumbnailUrl) {
      return (
        <div className={styles.fileIconFallback}>
          {getFileIcon(file.name)}
        </div>
      );
    }

    return (
      <img 
        src={thumbnailUrl} 
        alt={file.name}
        className={styles.fileThumbnail}
      />
    );
  };

  const FileViewer = ({ file }) => {
    const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState(null);
    
    const ext = (file && file.name && typeof file.name === 'string') ? file.name.split('.').pop()?.toLowerCase() || '' : '';
    const isPDF = ext === 'pdf';
    const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'heic', 'heif'].includes(ext);
    const isVideo = ['mp4', 'avi', 'mov', 'wmv', 'mkv', 'webm', 'flv', 'm4v'].includes(ext);

    useEffect(() => {
      let mounted = true;
      let objectUrl = null;

      const loadPreview = async () => {
        setPreviewLoading(true);
        setPreviewError(null);

        // Construir posibles URLs para intentar
        const timestamp = Date.now();
        const possibleUrls = [];
        
        // Si la URL es absoluta, usarla
        if (file.url.startsWith('http')) {
          possibleUrls.push(`${file.url}${file.url.includes('?') ? '&' : '?'}t=${timestamp}`);
        } else {
          // URL relativa del backend - probar múltiples formatos
          const relativeUrl = file.url.startsWith('/') ? file.url : `/${file.url}`;
          possibleUrls.push(`${API_BASE_URL}${relativeUrl}${relativeUrl.includes('?') ? '&' : '?'}t=${timestamp}`);
        }

        // Intentar cada URL hasta que una funcione
        const token = localStorage.getItem('authToken');
        for (const url of possibleUrls) {
          try {
            const res = await fetch(url, {
              headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            });

            if (!res.ok) {
              console.warn(`Failed to load from ${url}: ${res.status}`);
              continue;
            }

            let blob = await res.blob();
            // HEIC/HEIF conversion
            const lowerName = (file.name || '').toLowerCase();
            if (blob.type === 'image/heic' || blob.type === 'image/heif' || lowerName.endsWith('.heic') || lowerName.endsWith('.heif')) {
              try {
                const heic2any = (await import('heic2any')).default;
                const converted = await heic2any({ blob, toType: 'image/jpeg', quality: 0.85 });
                blob = Array.isArray(converted) ? converted[0] : converted;
              } catch (heicErr) {
                console.warn('HEIC conversion failed, using original blob:', heicErr);
              }
            }
            objectUrl = URL.createObjectURL(blob);
            if (!mounted) {
              URL.revokeObjectURL(objectUrl);
              return;
            }
            setPreviewBlobUrl(objectUrl);
            setPreviewLoading(false);
            setPreviewError(null);
            return;
          } catch (err) {
            console.error('Error loading preview url:', url, err);
            continue;
          }
        }

        if (mounted) {
          setPreviewError('No se pudo cargar la previsualización');
          setPreviewLoading(false);
        }
      };

      loadPreview();

      return () => {
        mounted = false;
        if (objectUrl) URL.revokeObjectURL(objectUrl);
      };
    }, [file]);

    if (previewLoading) {
      return <div className={styles.previewLoading}>Cargando previsualización...</div>;
    }

    if (previewError) {
      const downloadUrl = file.url.startsWith('http') ? file.url : `${API_BASE_URL}${file.url.startsWith('/') ? file.url : `/${file.url}`}`;
      return (
        <div className={styles.previewError}>
          <div>{previewError}</div>
          <a href={downloadUrl} target="_blank" rel="noopener noreferrer" className={styles.downloadLink}>
            📥 Descargar archivo
          </a>
        </div>
      );
    }

    const url = previewBlobUrl;

    return (
      <div className={styles.previewContainer}>
        {isImage && (
          <ZoomableImage src={url} alt={file.name} />
        )}
        {isPDF && (
          <iframe src={url} title={file.name} className={styles.previewIframe} />
        )}
        {isVideo && (
          <video src={url} controls className={styles.previewVideo}>
            Tu navegador no soporta la reproducción de videos.
          </video>
        )}
        {!isImage && !isPDF && !isVideo && (
          <div className={styles.previewUnavailable}>
            <div style={{ fontSize: '48px' }}>📄</div>
            <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{file.name}</div>
            <div>Previsualización no disponible</div>
            <a href={file.url.startsWith('http') ? file.url : `${API_BASE_URL}${file.url.startsWith('/') ? file.url : `/${file.url}`}`} target="_blank" rel="noopener noreferrer" className={styles.downloadLink}>
              📥 Descargar archivo
            </a>
          </div>
        )}
      </div>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "No definida";
    const date = new Date(dateString);
    
    // Detectar si es una cita "sin hora" (00:00 o 01:00)
    const hours = date.getHours();
    const minutes = date.getMinutes();
    if ((hours === 0 || hours === 1) && minutes === 0) {
      return date.toLocaleDateString("es-ES", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }) + " - Sin hora";
    }
    
    return date.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Detalles de la Cita" position={{ minWidth: '800px', maxWidth: '1000px' }}>
        <div className={styles.loadingContainer}>
          <p>Cargando información...</p>
        </div>
      </Modal>
    );
  }

  // Always render the Modal when open. If appointmentInfo is not yet loaded,
  // show a placeholder message so the modal UI is visible and easier to debug.
  if (!appointmentInfo) {
    console.log("Rendering AppointmentDetailModal (no data yet). isOpen:", isOpen, "citaId:", citaId, "loading:", loading);
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Detalles de la Cita" position={{ minWidth: '800px', maxWidth: '1000px' }}>
        <div className={styles.loadingContainer}>
          <p>{loading ? "Cargando información..." : "No hay información disponible para esta cita."}</p>
        </div>
      </Modal>
    );
  }

  console.log("Rendering AppointmentDetailModal with data:", appointmentInfo);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Detalles de la Cita" position={{ minWidth: '800px', maxWidth: '1000px', paddingTop: "16px" }}>
      <div className={styles.modalContent}>
        {/* General Information */}
        <FormSection title="Información General">
          <div className={styles.infoGrid}>
            <div className={styles.infoColumn}>
              <div className={styles.infoItem}>
                <label>Expediente:</label>
                <span>{appointmentInfo.expedienteId}</span>
              </div>
              <div className={styles.infoItem}>
                <label>Fecha Inicio:</label>
                <span>{formatDate(appointmentInfo.fechaCita)}</span>
              </div>
              <div className={styles.infoItem}>
                <label>Fecha Fin:</label>
                <span>{formatDate(appointmentInfo.fechaCitaFin)}</span>
              </div>
            </div>
            <div className={styles.infoColumn}>
             
              <div className={styles.infoItemRight}>
                <label>Localidad:</label>
                <span>{appointmentInfo.localidadCliente}</span>
              </div>
              <div className={styles.infoItemRight}>
                <label>Domicilio:</label>
                <span>{appointmentInfo.domicilioCliente}</span>
              </div>
            </div>
          </div>
          {appointmentInfo.info && (
            <div className={styles.infoItem}>
              <label>Información Adicional:</label>
              <p className={styles.infoText}>{appointmentInfo.info}</p>
            </div>
          )}
        </FormSection>

        {/* Contacts */}
        {appointmentInfo.contactos && appointmentInfo.contactos.length > 0 && (
          <FormSection title="Contactos">
            <div className={styles.contactsList}>
              {appointmentInfo.contactos.map((contact) => (
                <ContactItem
                  key={contact.contactoId}
                  contact={{
                    id: contact.contactoId,
                    name: contact.nombre,
                    phone: contact.telefono,
                    address: contact.piso || "",
                    info: contact.info,
                    rol: { label: contact.contactoRol }
                  }}
                  hideActions={true}
                />
              ))}
            </div>
          </FormSection>
        )}

        {/* Comentarios */}
        {appointmentInfo.comentarios && appointmentInfo.comentarios.length > 0 && (
          <FormSection title="Comentarios">
            <div className={styles.commentsSection}>
              {appointmentInfo.comentarios.map((comentario, index) => (
                <div key={index} className={styles.commentItem}>
                  <div className={styles.commentHeader}>
                    <MyIcons name="chat" width={16} height={16} color="var(--main)" />
                    <span className={styles.commentDate}>
                      {comentario.fecha ? new Date(comentario.fecha).toLocaleDateString("es-ES", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit"
                      }) : "Fecha no disponible"}
                    </span>
                  </div>
                  <p className={styles.commentText}>{comentario.texto || comentario.comentario || comentario}</p>
                </div>
              ))}
            </div>
          </FormSection>
        )}

        {/* Archivos Existentes - Botones con thumbnails */}
        {(appointmentInfo.archivosFotos?.length > 0 || 
          appointmentInfo.archivosPresupuestos?.length > 0 || 
          appointmentInfo.archivosFirmas?.length > 0 ||
          appointmentInfo.archivosVisibles?.length > 0) && (
          <FormSection title="Archivos Adjuntos">
            <div className={styles.fileButtonsGrid}>
              {appointmentInfo.archivosPresupuestos?.length > 0 && (
                <button 
                  className={styles.fileButton}
                  onClick={() => handleOpenFilesModal('presupuestos')}
                >
                  <div className={styles.fileButtonThumbnail}>
                    <div className={styles.fileIconFallback}>
                      <MyIcons name="fileFilled" width={40} height={40} color="#dc2626" />
                    </div>
                  </div>
                  <div className={styles.fileButtonInfo}>
                    <span className={styles.fileButtonTitle}>Presupuestos</span>
                    <span className={styles.fileButtonCount}>{appointmentInfo.archivosPresupuestos.length} archivo(s)</span>
                  </div>
                  <MyIcons name="arrowRight" width={20} height={20} color="var(--main)" />
                </button>
              )}
              {appointmentInfo.archivosFotos?.length > 0 && (
                <button 
                  className={styles.fileButton}
                  onClick={() => handleOpenFilesModal('fotos')}
                >
                  <div className={styles.fileButtonThumbnail}>
                    {isImageFile(appointmentInfo.archivosFotos[0].name) ? (
                      <FileThumbnail file={appointmentInfo.archivosFotos[0]} />
                    ) : (
                      <div className={styles.fileIconFallback}>
                        <MyIcons name="imageFilled" width={40} height={40} color="#059669" />
                      </div>
                    )}
                  </div>
                  <div className={styles.fileButtonInfo}>
                    <span className={styles.fileButtonTitle}>Fotos</span>
                    <span className={styles.fileButtonCount}>{appointmentInfo.archivosFotos.length} archivo(s)</span>
                  </div>
                  <MyIcons name="arrowRight" width={20} height={20} color="var(--main)" />
                </button>
              )}
              {appointmentInfo.archivosFirmas?.length > 0 && (
                <button 
                  className={styles.fileButton}
                  onClick={() => handleOpenFilesModal('firmas')}
                >
                  <div className={styles.fileButtonThumbnail}>
                    {isImageFile(appointmentInfo.archivosFirmas[0].name) ? (
                      <FileThumbnail file={appointmentInfo.archivosFirmas[0]} />
                    ) : (
                      <div className={styles.fileIconFallback}>
                        <MyIcons name="fileFilled" width={40} height={40} color="#2563eb" />
                      </div>
                    )}
                  </div>
                  <div className={styles.fileButtonInfo}>
                    <span className={styles.fileButtonTitle}>Firmas</span>
                    <span className={styles.fileButtonCount}>{appointmentInfo.archivosFirmas.length} archivo(s)</span>
                  </div>
                  <MyIcons name="arrowRight" width={20} height={20} color="var(--main)" />
                </button>
              )}
              {appointmentInfo.archivosVisibles?.length > 0 && (
                <button 
                  className={styles.fileButton}
                  onClick={() => handleOpenFilesModal('visibles')}
                >
                  <div className={styles.fileButtonThumbnail}>
                    {isImageFile(appointmentInfo.archivosVisibles[0].name) ? (
                      <FileThumbnail file={appointmentInfo.archivosVisibles[0]} />
                    ) : (
                      <div className={styles.fileIconFallback}>
                        <MyIcons name="fileFilled" width={40} height={40} color="var(--main)" />
                      </div>
                    )}
                  </div>
                  <div className={styles.fileButtonInfo}>
                    <span className={styles.fileButtonTitle}>Archivos administración</span>
                    <span className={styles.fileButtonCount}>{appointmentInfo.archivosVisibles.length} archivo(s)</span>
                  </div>
                  <MyIcons name="arrowRight" width={20} height={20} color="var(--main)" />
                </button>
              )}
            </div>
          </FormSection>
        )}

      {/* Modal de Archivos */}
      {filesModalOpen && (
        <Modal 
          isOpen={filesModalOpen} 
          onClose={handleCloseFilesModal} 
          title={filesModalCategory === 'presupuestos' ? 'Presupuestos' : filesModalCategory === 'fotos' ? 'Fotos' : filesModalCategory === 'visibles' ? 'Archivos de administración' : 'Firmas'}
          position={{ minWidth: '800px', maxWidth: '1000px' }}
        >
          {selectedFile ? (
            <div className={styles.viewerWrapper}>
              <div className={styles.viewerHeader}>
                <button className={styles.backButton} onClick={handleCloseViewer}>
                  <MyIcons name="arrowLeft" width={20} height={20} color="var(--black)" />
                  <span>Volver</span>
                </button>
                <h4 className={styles.viewerTitle}>{selectedFile.name}</h4>
              </div>
              <FileViewer file={selectedFile} />
            </div>
          ) : (
            <div className={styles.filesModalContent}>
              <div className={styles.filesGrid}>
                {getFilesForCategory().map((file, index) => {
                  const isImage = isImageFile(file.name);
                  
                  return (
                    <div key={index} className={styles.fileItem} onClick={() => handleFileClick(file)}>
                      <div className={styles.fileIconContainer}>
                        {isImage ? (
                          <FileThumbnail file={file} />
                        ) : (
                          <div className={styles.fileIconFallback}>
                            {getFileIcon(file.name)}
                          </div>
                        )}
                      </div>
                      <div className={styles.fileName}>{file.name}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </Modal>
      )}

        {/* Actions */}
        <h3 className={styles.sectionTitle}>Acciones</h3>
        <div className={styles.actionsGrid}>
          {/* Upload Budget */}
          <FormSection title="Agregar Presupuesto">
              <input
                ref={fileInputRefBudget}
                type="file"
                multiple
                accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                onChange={handleBudgetFileSelect}
                disabled={uploading}
                style={{ display: "none" }}
              />
              {selectedBudgetFiles && selectedBudgetFiles.length > 0 && (
                <div className={styles.selectedFiles}>
                  <div className={styles.selectedFilesHeader}>
                    <p>{selectedBudgetFiles.length} archivo(s) seleccionado(s)</p>
                    <button 
                      type="button"
                      className={styles.clearButton} 
                      onClick={handleClearBudgetFiles}
                      disabled={uploading}
                    >
                      <MyIcons name="close" width={16} height={16} color="var(--red)" />
                    </button>
                  </div>
                  {Array.from(selectedBudgetFiles).map((file, index) => (
                    <div key={index} className={styles.fileName}>{file.name}</div>
                  ))}
                </div>
              )}
              <Button
                onClick={selectedBudgetFiles ? handleUploadBudget : () => fileInputRefBudget.current?.click()}
                disabled={uploading}
                style={{ maxWidth: "220px" }}
              >
                {selectedBudgetFiles ? "Guardar" : "Seleccionar Archivos"}
              </Button>
            </FormSection>

          {/* Upload Photos */}
          <FormSection title="Agregar Fotos">
              <input
                ref={fileInputRefPhotos}
                type="file"
                multiple
                accept="image/*"
                onChange={handlePhotoFileSelect}
                disabled={uploading}
                style={{ display: "none" }}
              />
              {selectedPhotoFiles && selectedPhotoFiles.length > 0 && (
                <div className={styles.selectedFiles}>
                  <div className={styles.selectedFilesHeader}>
                    <p>{selectedPhotoFiles.length} foto(s) seleccionada(s)</p>
                    <button 
                      type="button"
                      className={styles.clearButton} 
                      onClick={handleClearPhotoFiles}
                      disabled={uploading}
                    >
                      <MyIcons name="close" width={16} height={16} color="var(--red)" />
                    </button>
                  </div>
                  {Array.from(selectedPhotoFiles).map((file, index) => (
                    <div key={index} className={styles.fileName}>{file.name}</div>
                  ))}
                </div>
              )}
              <Button
                onClick={selectedPhotoFiles ? handleUploadPhotos : () => fileInputRefPhotos.current?.click()}
                disabled={uploading}
                style={{ maxWidth: "220px" }}
              >
                {selectedPhotoFiles ? "Guardar" : "Seleccionar Fotos"}
              </Button>
            </FormSection>

          {/* Upload Comments */}
          <FormSection title="Agregar Comentarios">
              <Input
                name="comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Escriba su comentario"
                disabled={uploading}
              />
              <Button
                onClick={handleUploadComments}
                disabled={uploading || !comment.trim()}
                style={{ maxWidth: "220px", marginTop: "8px" }}
              >
                Guardar
              </Button>
            </FormSection>

          {/* Upload Signature */}
          <FormSection title="Agregar Firma">
              {!showSignaturePad ? (
                <Button
                  onClick={() => setShowSignaturePad(true)}
                  disabled={uploading}
                  style={{ maxWidth: "220px" }}
                >
                  Abrir Panel de Firma
                </Button>
              ) : (
                <div className={styles.signatureContainer}>
                  <SignatureCanvas
                    ref={sigCanvas}
                    canvasProps={{
                      className: styles.signatureCanvas,
                    }}
                  />
                  <div className={styles.signatureButtons}>
                    <Button
                      onClick={clearSignature}
                      disabled={uploading}
                      style={{ flex: 1 }}
                    >
                      Limpiar
                    </Button>
                    <Button
                      onClick={handleUploadSignature}
                      disabled={uploading}
                      style={{ flex: 1 }}
                    >
                      Guardar Firma
                    </Button>
                  </div>
                </div>
              )}
            </FormSection>
        </div>

        {/* Status indicators */}
        <div className={styles.statusGrid}>
          {appointmentInfo.tienePresupuesto && (
            <div className={styles.statusItem}>
              <MyIcons name="check" width={16} height={16} color="var(--green)" />
              <span>Presupuesto agregado</span>
            </div>
          )}
          {appointmentInfo.tieneFotos && (
            <div className={styles.statusItem}>
              <MyIcons name="check" width={16} height={16} color="var(--green)" />
              <span>Fotos agregadas</span>
            </div>
          )}
          {appointmentInfo.tieneComentarios && (
            <div className={styles.statusItem}>
              <MyIcons name="check" width={16} height={16} color="var(--green)" />
              <span>Comentarios agregados</span>
            </div>
          )}
          {appointmentInfo.tieneFirmas && (
            <div className={styles.statusItem}>
              <MyIcons name="check" width={16} height={16} color="var(--green)" />
              <span>Firma agregada</span>
            </div>
          )}
        </div>

        {/* Close Appointment Button - No mostrar en historial o si la cita ya está cerrada */}
        {!isHistory && appointmentInfo.estadoCita !== 'CERRADA' && appointmentInfo.estadoCita !== 'CERRADO' && (
          <div className={styles.closeAppointmentContainer}>
            <Button
              onClick={handleCloseCita}
              disabled={closing || uploading}
              style={{
                backgroundColor: "var(--red)",
                maxWidth: "300px",
                marginTop: "16px",
              }}
            >
              {closing ? "Cerrando..." : "Cerrar Cita"}
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default AppointmentDetailModal;
